import clsx from 'clsx'
import {FC, useEffect, useMemo, useState} from 'react'
import PlatformFieldDataSearchSelect, {
  resolveSavedDestinationPlatformDataId,
} from 'src/app/pages/listing/components/PlatformFieldDataSearchSelect'
import InfoTooltip from '../common/InfoTooltip'
import TimeRangePickerInput from './TimeRangePickerInput'
import {
  FIELD_MAPPING_DATA,
} from 'src/interfaces/Platform.interface'
import {matchesIndependentFieldMappingSlot, resolveFieldMappingIsRequired} from 'src/utils/fieldMappingSaveUtils'
import {isSettingFileActionMapping} from 'src/utils/settingFileFieldMappings'
import MappingShortcodeChips from 'src/app/pages/listing/components/MappingShortcodeChips'
import {
  resolveFieldMappingShortcodeSettingName,
  shouldShowFieldMappingShortcodes,
} from 'src/utils/platformSettingShortcodeUtils'
import {
  getIndependentMappingWrapperClass,
  getSequenceFieldWrapperClass,
  getSequenceIndependentMappingShellClass,
} from 'src/utils/channelSettingSequenceLayout'
import {useApplyPlatformSettingShortcodes} from 'src/utils/usePlatformSettingDisplayText'
import SequenceFieldShell from 'src/app/pages/listing/components/SequenceFieldShell'
import {useChannelSettingLinkedPanelLayout} from 'src/app/pages/listing/components/ChannelSettingLinkedPanelLayoutContext'

const IndependentMapping: FC<{
  savedMappings: any
  setSavedMappings: any
  sourceSide?: number
  destinationSide?: number
  selectedPlatformId: number
  isMappingVisible: boolean
  handleChange: (e: React.ChangeEvent<any>) => void
  field_mapping: any
  savedMapping: any
  settingName: string
  settingId: number | null
  setFieldMappings: React.Dispatch<React.SetStateAction<FIELD_MAPPING_DATA[]>>
  fieldMappings: FIELD_MAPPING_DATA[]
  sourcePlatform?: string
  destinationPlatform?: string
  index: number
  isRequiredMissing?: boolean
}> = ({
  field_mapping,
  isMappingVisible,
  savedMappings,
  setSavedMappings,
  settingId,
  settingName,
  setFieldMappings,
  fieldMappings,
  sourcePlatform,
  destinationPlatform,
  index,
  isRequiredMissing = false,
  savedMapping,
  selectedPlatformId,
}) => {
  const formatSettingDisplayText = useApplyPlatformSettingShortcodes()
  const isFieldRequired = useMemo(
    () => resolveFieldMappingIsRequired(field_mapping, {savedMapping}),
    [field_mapping, savedMapping]
  )
  const pickupFieldKey = `${field_mapping?.field_code ?? ''} ${field_mapping?.api_code ?? ''} ${field_mapping?.display_name ?? ''}`.toLowerCase()
  const isPickupTimeMapping =
    pickupFieldKey.includes('pickup_time') ||
    pickupFieldKey.includes('pickup time') ||
    pickupFieldKey.includes('pickup time range') ||
    pickupFieldKey.includes('24 hrs')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [mapping, setMapping] = useState<any>([])
  const [inputValue, setInputValue] = useState('');
  const cloneMappingItem = (item: any) => (item ? JSON.parse(JSON.stringify(item)) : item)

  const appendPlatformFieldDataToField = (option: any) => {
    if (!option?.id || !field_mapping) {
      return
    }

    if (!Array.isArray(field_mapping.platformfielddata_set)) {
      field_mapping.platformfielddata_set = []
    }

    const exists = field_mapping.platformfielddata_set.some(
      (item: any) => Number(item?.id) === Number(option.id)
    )

    if (!exists) {
      field_mapping.platformfielddata_set = [...field_mapping.platformfielddata_set, option]
    }
  }

  const normalizeSelectDataSet = (fieldMappingDataSet: any[] = []) => {
    if (field_mapping?.input_type !== 'SELECT' && field_mapping?.input_type !== 'MULTISELECT') {
      return fieldMappingDataSet
    }

    const validOptionIds = new Set(
      (field_mapping?.platformfielddata_set || [])
        .map((option: any) => Number(option?.id))
        .filter((id) => !Number.isNaN(id))
    )

    fieldMappingDataSet.forEach((item) => {
      const selected = item?.destination_platform_data
      const selectedId = resolveSavedDestinationPlatformDataId(item) || null
      if (selectedId === null || selectedId === undefined || selectedId === '') {
        return
      }

      validOptionIds.add(Number(selectedId))
      if (typeof selected === 'object' && selected?.id) {
        appendPlatformFieldDataToField(selected)
      }
    })

    return fieldMappingDataSet.map((item: any) => {
      const selected = item?.destination_platform_data
      const selectedId = resolveSavedDestinationPlatformDataId(item) || null

      if (selectedId === null || selectedId === undefined || selectedId === '') {
        return item
      }

      if (typeof selected === 'object' && selected?.id) {
        appendPlatformFieldDataToField(selected)
        return {
          ...item,
          destination_platform_data: selected,
          destination_platform_data_id: selected.id,
          destination_platform_data_str: String(selected.id),
          hasPair: true,
        }
      }

      if (validOptionIds.has(Number(selectedId))) {
        const matched = field_mapping?.platformfielddata_set?.find(
          (opt: any) => Number(opt.id) === Number(selectedId)
        )

        if (matched) {
          return {
            ...item,
            destination_platform_data: matched,
            destination_platform_data_id: matched.id,
            destination_platform_data_str: String(matched.id),
            hasPair: true,
          }
        }

        return {
          ...item,
          destination_platform_data_id: Number(selectedId),
          destination_platform_data_str: String(selectedId),
          hasPair: true,
        }
      }

      return {
        ...item,
        destination_platform_data: '',
        destination_platform_data_id: null,
        destination_platform_data_str: '',
        hasPair: false,
      }
    })
  }

  const mapFieldMappingDataSetForSave = (rows: any[] = [], resolvedIsDefault: boolean) =>
    normalizeSelectDataSet(rows)?.map(
      ({source_platform_data, destination_platform_data, ...rest}) => ({
        ...rest,
        source_platform_data_id: source_platform_data?.id ?? null,
        destination_platform_data_id:
          destination_platform_data?.id ?? rest.destination_platform_data_id ?? null,
        destination_platform_data_str:
          destination_platform_data?.id != null
            ? String(destination_platform_data.id)
            : rest.destination_platform_data_str ?? '',
        hasPair: Boolean(
          destination_platform_data?.id ??
            rest.destination_platform_data_id ??
            rest.destination_platform_data_str
        ),
        isDefault: resolvedIsDefault,
      })
    )

  const mappingKey = useMemo(() => {
    if (field_mapping?.setting_mapping_id !== undefined && field_mapping?.setting_mapping_id !== null) {
      return `setting-${field_mapping.setting_mapping_id}`
    }
    if (field_mapping?.id) {
      return `field-${field_mapping.id}`
    }
    if (savedMapping?.id) {
      return `saved-${savedMapping.id}`
    }
    return `code-${field_mapping?.api_code ?? index}`
  }, [field_mapping?.setting_mapping_id, field_mapping?.id, field_mapping?.api_code, savedMapping?.id, index])
  const getMappingIdentifier = (mapping: any): string => {
    if (!mapping) return ''
    if (mapping.mappingKey) {
      return mapping.mappingKey
    }
    if (mapping.setting_mapping_id !== undefined && mapping.setting_mapping_id !== null) {
      return `setting-${mapping.setting_mapping_id}`
    }
    if (
      (mapping.destinationFieldId !== undefined && mapping.destinationFieldId !== null) ||
      (mapping.sourceFieldId !== undefined && mapping.sourceFieldId !== null)
    ) {
      return `destId-${mapping.destinationFieldId ?? 'null'}-srcId-${mapping.sourceFieldId ?? 'null'}`
    }
    const destinationCode = mapping?.destination_field?.api_code ?? 'dest-null'
    const sourceCode = mapping?.source_field?.api_code ?? 'source-null'
    return `${destinationCode}-${sourceCode}`
  }
  const matchesFieldMappingEntry = (item: any) => {
    if (!item || item.groupCode !== settingName) {
      return false
    }
    const itemIdentifier = getMappingIdentifier(item)
    const targetIdentifier =
      mappingKey ||
      getMappingIdentifier({
        mappingKey,
        destination_field: field_mapping,
        source_field: null,
        setting_mapping_id: field_mapping?.setting_mapping_id,
      })

    return itemIdentifier === targetIdentifier
  }

  useEffect(() => {
    if(!mapping?.length) return
    const currentValue =
      field_mapping.input_type === 'DATETIME'
        ? formatDateString(mapping[0]?.fieldmappingdata_set[0]?.destination_platform_data_str ?? '')
        : mapping[0]?.fieldmappingdata_set[0]?.destination_platform_data_str ?? '';
  
    setInputValue(currentValue ?? '');
  }, [mapping, field_mapping.input_type]);
  useEffect(() => {
    let saved_mapping
    let normalizedMapping: any[] | null = null
    if (savedMappings?.length > 0) {
      saved_mapping = savedMappings.filter((mapping: any) =>
        matchesIndependentFieldMappingSlot(mapping, field_mapping, {
          destinationPlatformId: field_mapping?.platform ?? null,
          settingId,
        })
      )
    }

      if (saved_mapping?.length > 0) {
        const cloned = saved_mapping.map(cloneMappingItem)
        if (!cloned[0]?.fieldmappingdata_set?.length) {
          cloned[0].fieldmappingdata_set = [
            {
              id: null,
              destination_platform_data: '',
              destination_platform_data_str: '',
              source_platform_data: '',
              source_platform_data_str: '',
              isDefault: field_mapping?.isDefault,
            },
          ];
        }
        cloned[0].fieldmappingdata_set = normalizeSelectDataSet(cloned[0].fieldmappingdata_set)
        cloned[0].mappingKey = cloned[0].mappingKey ?? mappingKey
        normalizedMapping = cloned
        setMapping(cloned);
      } else {
        const defaultMapping = [
          {
            id: null,
            setting_id: settingId,
            source_field: null,
            isDefault: field_mapping?.isDefault,
            destination_field: field_mapping,
            setting_mapping_id: field_mapping?.setting_mapping_id || null,
            fieldmappingdata_set: [
              {
                id: null,
                destination_platform_data: '',
                destination_platform_data_str: '',
                source_platform_data: '',
                source_platform_data_str: '',
                isDefault: field_mapping?.isDefault,
              },
            ],
            mappingKey,
          },
        ];
        normalizedMapping = defaultMapping.map(cloneMappingItem)
        setMapping(normalizedMapping);
      }

    if (normalizedMapping && normalizedMapping.length > 0) {
    const persistedRow = normalizedMapping[0]
    const resolvedIsDefault =
      field_mapping?.isDefault !== undefined && field_mapping?.isDefault !== null
        ? field_mapping.isDefault
        : persistedRow?.isDefault
    const resolvedSettingMappingId =
      field_mapping?.setting_mapping_id ?? persistedRow?.setting_mapping_id

    const field_mapping_data_set = mapFieldMappingDataSetForSave(
      normalizedMapping[0]?.fieldmappingdata_set || [],
      resolvedIsDefault
    );
    const hasSelection =
      ['TEXT', 'TEXTAREA'].includes(field_mapping?.input_type) ||
      field_mapping_data_set?.some(
        (item: any) =>
          item.destination_platform_data_id ||
          item.source_platform_data_id ||
          item.destination_platform_data_str ||
          item.source_platform_data_str
      );
    const hasPersistedMappingDataRow = field_mapping_data_set?.some(
      (item: any) => item?.id !== null && item?.id !== undefined
    )

    const hasExistingMappingRef = Boolean(
      normalizedMapping?.[0]?.id !== null && normalizedMapping?.[0]?.id !== undefined
    )

    const resolvedIsRequired = resolveFieldMappingIsRequired(field_mapping, {
      savedMapping: normalizedMapping?.[0],
    })

    setFieldMappings((prev) => {
  const targetIndex = prev.findIndex(matchesFieldMappingEntry);

  // Keep required mappings and already-saved mappings in state even when empty.
  if (
    !hasSelection &&
    !hasPersistedMappingDataRow &&
    !resolvedIsRequired &&
    !hasExistingMappingRef
  ) {
    if (targetIndex === -1) return prev;
    const updated = [...prev];
    updated.splice(targetIndex, 1);
    return updated;
  }

  const updatedObject = {
    field_mapping_id: normalizedMapping?.[0]?.id ?? null,
    groupCode: settingName,
    fieldCode: field_mapping.api_code,
    savedSettingId: settingId,
    isDefault: resolvedIsDefault,
    isRequired: resolvedIsRequired,
    savedMappingId: normalizedMapping?.[0]?.id ?? null,
    destinationFieldId: field_mapping?.id ?? null,
    setting_mapping_id: resolvedSettingMappingId,
    sourceFieldId: null,
    fieldMappingDataSet: field_mapping_data_set || [],
    mappingKey,
  };

  if (targetIndex !== -1) {
    const updated = [...prev];
    updated[targetIndex] = {
      ...updated[targetIndex],
      ...updatedObject,
    };
    return updated;
  } else {
    return [...prev, updatedObject];
  }
});

    }
  }, [savedMappings]);

  const updateSavedMappings = (updatedMapping: any) => {
    setSavedMappings((prevMappings) => {
      const newMappings = [...prevMappings]
      updatedMapping.forEach((mappingItem) => {
        if (!mappingItem) {
          return
        }

        const identifier = getMappingIdentifier({
          ...mappingItem,
          mappingKey: mappingItem.mappingKey ?? mappingKey,
        })

        const clonedItem = cloneMappingItem(mappingItem) || {}
        clonedItem.mappingKey = identifier

        if (mappingItem.id === null || mappingItem.id === undefined) {
          const existingIndex = newMappings.findIndex(
            (candidate) => getMappingIdentifier(candidate) === identifier
          )

          if (existingIndex === -1) {
            newMappings.push(clonedItem)
          } else {
            newMappings[existingIndex] = clonedItem
          }
        } else {
          const existingIndex = newMappings.findIndex((candidate) => candidate.id === mappingItem.id)

          if (existingIndex === -1) {
            newMappings.push(clonedItem)
          } else {
            newMappings[existingIndex] = clonedItem
          }
        }
      })
      return newMappings
    })
  }

  const validateField = (fieldName: string, value: any, isRequired: boolean) => {
    setErrors((prevErrors) => {
      const fieldKey = `${index}_${fieldName}`
      if (isRequired && (!value || (Array.isArray(value) && value.length === 0))) {
        return {...prevErrors, [fieldKey]: 'This field is required'}
      } else {
        const {[fieldKey]: _, ...newErrors} = prevErrors
        return newErrors
      }
    })
  }

  const formatDateString = (dateString: string) => {
    if (!dateString) return null
    const date = new Date(dateString.endsWith('Z') ? dateString.slice(0, -4) : dateString)
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
  }
  const handleInputChange = (
    value: any,
    field: string,
    dataField: string,
    isRequired: boolean,
    mapping_id,
    mappingId: number,
    selectedOptionData?: any
  ) => {
    validateField(field, value, isRequired)
    const updatedMapping = mapping.map((item: any) => {
      let updatedFieldMappingDataSet
      if (field_mapping.input_type === 'SELECT') {
        const selectedOption =
          value === null || value === undefined || value === ''
            ? null
            : (selectedOptionData ??
              field_mapping.platformfielddata_set.find((p) => p.id === value)) ||
              null
        if (selectedOption?.id) {
          appendPlatformFieldDataToField(selectedOption)
        }

        updatedFieldMappingDataSet = item.fieldmappingdata_set.map((fm: any) => ({
          ...fm,
          [field]: selectedOption,
          destination_platform_data_id: selectedOption?.id ?? null,
          destination_platform_data_str:
            selectedOption?.id != null ? String(selectedOption.id) : '',
          hasPair:
            value === 0 || value === '' || value === null || value === undefined
              ? false
              : true,
        }))
      } else if (field_mapping.input_type === 'MULTISELECT') {
        const selectedOptions = Array.isArray(selectedOptionData) ? selectedOptionData : []
        const previousRows = item.fieldmappingdata_set || []
        updatedFieldMappingDataSet = (Array.isArray(value) ? value : []).map((id, optionIndex) => {
          const selectedOption =
            selectedOptions[optionIndex] ??
            field_mapping.platformfielddata_set?.find((option: any) => Number(option.id) === Number(id)) ??
            null
          if (selectedOption?.id) {
            appendPlatformFieldDataToField(selectedOption)
          }
          const resolvedId = selectedOption?.id ?? id ?? null
          const hasValue = resolvedId !== null && resolvedId !== undefined && resolvedId !== ''
          const previous = previousRows.find(
            (row: any) => resolveSavedDestinationPlatformDataId(row) === String(resolvedId)
          )
          return {
            id: previous?.id ?? null,
            [field]: selectedOption,
            destination_platform_data_id: hasValue ? resolvedId : null,
            destination_platform_data_str: hasValue ? String(resolvedId) : '',
            hasPair: hasValue,
            isDefault: field_mapping?.isDefault,
          }
        })
      } else {
        updatedFieldMappingDataSet = item.fieldmappingdata_set.map((fm: any) => ({
          ...fm,
          [dataField]: value || null,
        }))
      }

      return {...item, fieldmappingdata_set: updatedFieldMappingDataSet}
    })
    updateSavedMappings(updatedMapping)

    let updatedFieldMappings: any = [...fieldMappings]
    const selectedValues =
      value === null || value === undefined || value === ''
        ? []
        : Array.isArray(value)
        ? value
        : [value]
    let existingFieldMappingIndex = updatedFieldMappings.findIndex(matchesFieldMappingEntry)
    let existingFieldMapping: any =
      existingFieldMappingIndex !== -1 ? updatedFieldMappings[existingFieldMappingIndex] : null

    if (selectedValues?.length === 0) {
      const emptyEntry = {
        id: mappingId,
        [dataField]: null,
        [`${dataField}_str`]: '',
        destination_platform_data_str: '',
        isDefault: field_mapping?.isDefault,
      }

      if (!existingFieldMapping) {
        const newEntry = {
          field_mapping_id: mapping[0]?.id,
          groupCode: settingName,
          fieldCode: field_mapping.api_code,
          savedSettingId: settingId,
          isDefault: field_mapping?.isDefault,
          isRequired: isFieldRequired,
          savedMappingId: mapping[0]?.id ?? null,
          destinationFieldId: field_mapping?.id ?? null,
          sourceFieldId: null,
          setting_mapping_id: field_mapping?.setting_mapping_id,
          fieldMappingDataSet: [emptyEntry],
          mappingKey,
        }
        updatedFieldMappings.push(newEntry)
        setFieldMappings(updatedFieldMappings)
        setMapping(updatedMapping.map(cloneMappingItem))
      } else {
        existingFieldMapping.fieldMappingDataSet = [emptyEntry]
        updatedFieldMappings[existingFieldMappingIndex] = existingFieldMapping
        setFieldMappings(updatedFieldMappings)
        setMapping(updatedMapping.map(cloneMappingItem))
      }
    } else {
      if (!existingFieldMapping) {
        const newEntry = {
          field_mapping_id: mapping[0]?.id,
          groupCode: settingName,
          fieldCode: field_mapping.api_code,
          savedSettingId: settingId,
          isDefault: field_mapping?.isDefault,
          isRequired: isFieldRequired,
          savedMappingId: mapping[0]?.id ?? null,
          destinationFieldId: field_mapping?.id ?? null,
          sourceFieldId: null,
          setting_mapping_id: field_mapping?.setting_mapping_id,
          fieldMappingDataSet: [],
          mappingKey,
        }
        updatedFieldMappings.push(newEntry)
        existingFieldMappingIndex = updatedFieldMappings.length - 1
        existingFieldMapping = newEntry
      }

      if (existingFieldMapping) {
        existingFieldMapping.mappingKey = mappingKey
      }

      if (field_mapping.input_type === 'MULTISELECT' && existingFieldMapping) {
        const previousRows = existingFieldMapping.fieldMappingDataSet || []
        existingFieldMapping.fieldMappingDataSet = selectedValues.map((val: any) => {
          const previous = previousRows.find(
            (row: any) =>
              Number(row?.[dataField]) === Number(val) ||
              resolveSavedDestinationPlatformDataId(row) === String(val)
          )
          const hasValue = val !== '' && val !== null && val !== undefined
          return {
            id: previous?.id ?? null,
            [dataField]: hasValue ? val : null,
            destination_platform_data_str: hasValue ? String(val) : '',
            hasPair: hasValue,
            isDefault: field_mapping?.isDefault,
          }
        })
      } else {
        selectedValues.forEach((val) => {
          if (!existingFieldMapping) {
            return
          }

          const isValueExists = existingFieldMapping.fieldMappingDataSet.some(
            (item: any) => item[dataField] === val
          )

          if (!isValueExists || field_mapping.input_type === 'SELECT') {
            const nextValue = val ?? ''
            existingFieldMapping.fieldMappingDataSet[0] =
              existingFieldMapping.fieldMappingDataSet[0] || {}
            existingFieldMapping.fieldMappingDataSet[0]['id'] = mappingId
            existingFieldMapping.fieldMappingDataSet[0][dataField] =
              nextValue === '' || nextValue === 0 ? null : nextValue
            existingFieldMapping.fieldMappingDataSet[0][`${dataField}_str`] =
              nextValue === '' || nextValue === 0 ? '' : String(nextValue)
            existingFieldMapping.fieldMappingDataSet[0]['destination_platform_data_str'] =
              nextValue === '' || nextValue === 0 ? '' : String(nextValue)
            existingFieldMapping.fieldMappingDataSet[0]['hasPair'] =
              nextValue !== '' && nextValue !== 0 && nextValue !== null && nextValue !== undefined
            existingFieldMapping.fieldMappingDataSet[0]['isDefault'] = field_mapping?.isDefault
          }
        })
      }

      updatedFieldMappings[existingFieldMappingIndex] = existingFieldMapping
      setFieldMappings(updatedFieldMappings)
      setMapping(updatedMapping.map(cloneMappingItem))
    }
  }

  const renderInputField = (dataField = 'destination_platform_data') => {
    switch (field_mapping?.input_type) {
      case 'SELECT':
        return (
          <PlatformFieldDataSearchSelect
            field={field_mapping}
            className="react-select"
            classNamePrefix="select"
            inputId={`independent-select-${index}`}
            instanceId={`independent-select-${index}`}
            isSearchable
            isClearable
            placeholder="Select a default option"
            options={field_mapping?.platformfielddata_set?.slice(0, 100)?.map((item: any) => ({
              value: item.id,
              label: item.api_name,
            }))}
            value={(() => {
              const selected = mapping[0]?.fieldmappingdata_set?.[0]?.destination_platform_data
              if (!selected || selected === '') {
                return null
              }

              if (typeof selected === 'object' && selected?.id) {
                return {
                  value: selected.id,
                  label: selected.api_name ?? selected.display_name ?? String(selected.id),
                  raw: selected,
                }
              }

              const matchedOption = field_mapping?.platformfielddata_set?.find(
                (item: any) => Number(item.id) === Number(selected)
              )

              if (matchedOption) {
                return {
                  value: matchedOption.id,
                  label: matchedOption.api_name,
                  raw: matchedOption,
                }
              }

              return {
                value: Number(selected),
                label: String(selected),
                raw: {id: Number(selected)},
              }
            })()}
            onChange={(selectedOption) =>
              handleInputChange(
                selectedOption?.value ?? null,
                'destination_platform_data',
                `${dataField}_id`,
                isFieldRequired,
                mapping[0]?.id,
                mapping[0].fieldmappingdata_set[0].id,
                selectedOption?.raw
              )
            }
            styles={{
              menu: (base) => ({
                ...base,
                zIndex: 999999,
                position: 'absolute',
              }),
              menuList: (base) => ({
                ...base,
                zIndex: 999999,
              }),
            }}
          />
        )

      case 'MULTISELECT': {
        const entries = mapping[0]?.fieldmappingdata_set ?? []
        const currentIds = entries
          .map((item: any) => resolveSavedDestinationPlatformDataId(item))
          .filter((id: string) => id !== '')

        return (
          <PlatformFieldDataSearchSelect
            field={field_mapping}
            isMulti
            inputId={`independent-multiselect-${index}`}
            instanceId={`independent-multiselect-${index}`}
            options={field_mapping?.platformfielddata_set?.slice(0, 100)?.map((item: any) => ({
              value: item.id,
              label: item.api_name,
              raw: item,
            }))}
            value={currentIds
              .map((selectedId: string) => {
                const selectedEntry = entries.find(
                  (item: any) => resolveSavedDestinationPlatformDataId(item) === selectedId
                )?.destination_platform_data

                if (typeof selectedEntry === 'object' && selectedEntry?.id) {
                  return {
                    value: selectedEntry.id,
                    label: selectedEntry.api_name ?? selectedEntry.display_name ?? String(selectedEntry.id),
                    raw: selectedEntry,
                  }
                }

                const matchedOption = field_mapping?.platformfielddata_set?.find(
                  (opt: any) => Number(opt.id) === Number(selectedId)
                )

                if (matchedOption) {
                  return {value: matchedOption.id, label: matchedOption.api_name, raw: matchedOption}
                }

                const numericId = Number(selectedId)
                if (Number.isNaN(numericId)) {
                  return null
                }

                return {value: numericId, label: String(numericId)}
              })
              .filter(Boolean)}
            onChange={(selectedOptions) => {
              const selectedValues = selectedOptions?.map((opt: any) => opt.value) || []
              handleInputChange(
                selectedValues,
                'destination_platform_data',
                `${dataField}_id`,
                isFieldRequired,
                mapping[0]?.id,
                mapping[0]?.fieldmappingdata_set?.[0]?.id,
                selectedOptions?.map((opt: any) => opt.raw)
              )
            }}
            styles={{
              menu: (base) => ({
                ...base,
                zIndex: 999999,       
                position: "absolute", 
              }),
              menuList: (base) => ({
                ...base,
                zIndex: 999999,
              }),
            }}
          />
        )
      }

      case 'BOOLEAN':
        return (
          <div className='form-check form-switch form-check-solid'>
            <input
              type='checkbox'
              className='form-check-input h-30px w-50px'
              checked={
                mapping[0]?.fieldmappingdata_set[0]?.destination_platform_data_str === 'true'
              }
              required={isFieldRequired || false}
              onChange={(e) =>
                handleInputChange(
                  e.target.checked ? 'true' : 'false',
                  'destination_platform_data_str',
                  `${dataField}_str`,
                  isFieldRequired,
                  mapping[0]?.id,
                  mapping[0].fieldmappingdata_set[0].id
                )
              }
            />
          </div>
        )

      case 'DATETIME':
      case 'INTEGER':
      case 'TEXTAREA':
      case 'TEXT':
        return isPickupTimeMapping && field_mapping.input_type === 'TEXT' ? (
          <TimeRangePickerInput
            label={field_mapping.display_name}
            message={field_mapping?.description ?? ''}
            hideLabel
            containerClasses=''
            name={`${dataField}_str`}
            id={`ind-pickup-${index}`}
            value={inputValue ?? ''}
            required={isFieldRequired || false}
            onChange={(e) => {
              const newVal = e.target.value
              setInputValue(newVal ?? '')
              handleInputChange(
                newVal,
                'destination_platform_data_str',
                `${dataField}_str`,
                isFieldRequired,
                mapping[0]?.id,
                mapping[0].fieldmappingdata_set[0].id
              )
            }}
          />
        ) : (
          <input
            type={
              field_mapping.input_type === 'DATETIME'
                ? 'datetime-local'
                : field_mapping.input_type === 'INTEGER'
                ? 'number'
                : 'text'
            }
            placeholder={
              field_mapping.input_type === 'TEXTAREA' ? 'Add a description' : 'Enter value'
            }
            className={`form-control form-control-lg ${
              errors[`${index}_destination_platform_data_str`] ? 'border border-danger' : ''
            }`}
            value={inputValue}
            required={isFieldRequired || false}
            onChange={(e) => {
              const newVal = e.target.value;
              setInputValue(newVal ?? '');
              handleInputChange(
                newVal,
                'destination_platform_data_str',
                `${dataField}_str`,
                isFieldRequired,
                mapping[0]?.id,
                mapping[0].fieldmappingdata_set[0].id
              )
            }}
          />
        )

      default:
        return null
    }
  }
  const isBooleanMapping = field_mapping?.input_type === 'BOOLEAN'
  const useAdvancedGridCell = Boolean(field_mapping?.is_advance_setting)
  const insideLinkedPanel = useChannelSettingLinkedPanelLayout()
  const useGridCellLayout = useAdvancedGridCell || insideLinkedPanel

  if (isSettingFileActionMapping(field_mapping)) {
    return null
  }

  const mappingLabel = (
    <label className={`form-label ${isFieldRequired ? 'required' : ''}`}>
      {formatSettingDisplayText(field_mapping.display_name)}
      {field_mapping?.description ? (
        <>
          &nbsp;&nbsp;
          <InfoTooltip message={field_mapping.description} />
        </>
      ) : null}
    </label>
  )
  const mappingMeta = shouldShowFieldMappingShortcodes(field_mapping) ? (
    <MappingShortcodeChips
      platformId={selectedPlatformId}
      settingName={resolveFieldMappingShortcodeSettingName(field_mapping)}
      shortcodeOptions={field_mapping?.shortcode_options}
    />
  ) : null

  const mappingContent = (
    <div
      className={clsx(
        getSequenceIndependentMappingShellClass({useAdvancedGridCell: useGridCellLayout}),
        isBooleanMapping && 'advanced-boolean-mapping',
        !useGridCellLayout && isRequiredMissing && 'mapping-required-missing'
      )}
    >
      {!isBooleanMapping ? (
        <SequenceFieldShell
          label={mappingLabel}
          control={renderInputField('destination_platform_data')}
          meta={mappingMeta ?? undefined}
        />
      ) : (
        <>
          <div className='advanced-boolean-mapping__label'>{mappingLabel}</div>
          {renderInputField('destination_platform_data')}
          {mappingMeta}
        </>
      )}
    </div>
  )

  if (useGridCellLayout) {
    return (
      <div
        className={clsx(
          getSequenceFieldWrapperClass('col-12 col-sm-6', {insideLinkedPanel}),
          'advanced-independent-mapping',
          isRequiredMissing && 'mapping-required-missing'
        )}
      >
        {mappingContent}
      </div>
    )
  }

  return (
    <div className={getIndependentMappingWrapperClass({insideLinkedPanel})}>{mappingContent}</div>
  )
}

export default IndependentMapping

